import { FiExternalLink } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/config';
import { Button, cn, Dialog, TextArea } from '@/core/ui';
import { useContentReview } from '@/store/content-review';
import { getDbTypeHashFragment } from '@/utils';

type FeedbackEditModalProps = {
  onSave: (reviewId: string, comment: string) => Promise<void>;
  isLoading: boolean;
};

export const FeedbackEditModal = ({ onSave, isLoading }: FeedbackEditModalProps) => {
  const isFeedbackModalOpen = useContentReview.use.isFeedbackModalOpen();
  const feedbackReviewId = useContentReview.use.feedbackReviewId();
  const feedbackValue = useContentReview.use.feedbackValue();
  const closeFeedbackModal = useContentReview.use.closeFeedbackModal();
  const setFeedbackValue = useContentReview.use.setFeedbackValue();
  const feedbackType = useContentReview.use.feedbackType();

  const isComment = feedbackType === 'comment';

  const [reasonMainText, reasonMeta] = (() => {
    if (feedbackType !== 'reason' || !feedbackValue)
      return ['', [] as Array<{ label: string; value: string }>];

    const sepIndex = feedbackValue.indexOf('---');
    const rawMain = sepIndex === -1 ? feedbackValue : feedbackValue.slice(0, sepIndex);
    const rawMeta = sepIndex === -1 ? '' : feedbackValue.slice(sepIndex + 3);

    const metaLines = rawMeta
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    // Only generated reasons have a `label: value` block after `---`; free text (notes) is shown whole
    if (!metaLines.every((line) => line.includes(':'))) {
      return [feedbackValue.trim(), [] as Array<{ label: string; value: string }>];
    }
    const main = rawMain.trim();

    const meta = metaLines.map((line) => {
      const [label, ...rest] = line.split(':');
      return {
        label: label.trim(),
        value: rest.join(':').trim() || '-',
      };
    });

    return [main, meta];
  })();

  const handleSave = async () => {
    if (feedbackReviewId) {
      await onSave(feedbackReviewId, feedbackValue);
      closeFeedbackModal();
    }
  };

  return (
    <Dialog
      size='4xl'
      radius='sm'
      title={isComment ? 'Edit Comment' : 'Reason details'}
      isOpen={isFeedbackModalOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          closeFeedbackModal();
        }
      }}
      trigger={() => <></>}
      footer={(onClose) => (
        <div className={cn('flex justify-end gap-2', { hidden: !isComment })}>
          <Button
            variant='light'
            onPress={() => {
              closeFeedbackModal();
              onClose();
            }}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button color='primary' onPress={handleSave} isLoading={isLoading}>
            Save
          </Button>
        </div>
      )}
    >
      {isComment ? (
        <TextArea
          variant='bordered'
          value={feedbackValue}
          onValueChange={setFeedbackValue}
          placeholder='Enter feedback...'
          minRows={15}
          className='w-full'
        />
      ) : (
        <div className='space-y-6 max-h-[70vh] overflow-y-auto pr-1 mt-4'>
          <div className='bg-content1 border border-warning-500 rounded-md p-4'>
            <h3 className='font-semibold text-warning-700 mb-2'>Reason</h3>
            <p className='whitespace-pre-wrap text-sm text-default-800 leading-relaxed'>
              {reasonMainText || '---'}
            </p>
          </div>

          {reasonMeta.length > 0 && (
            <div className='bg-content1 border border-default-500 rounded-md p-4'>
              <h3 className='text-sm font-semibold text-default-700 mb-3'>Additional details</h3>
              <div className='grid grid-cols-1 divide-y divide-default-200 md:grid-cols-3 gap-x-6 gap-y-2 text-sm'>
                {reasonMeta.map((item) => {
                  const isQuestionId =
                    item.label?.toLowerCase() === 'questionid' || item.label?.toLowerCase() === 'question id';

                  // Link config based on file_context_0 pattern
                  let questionLink: string | null = null;
                  if (isQuestionId && item.value) {
                    // The dbType/hash might not be directly available here; use empty string or supply your logic
                    questionLink =
                      ROUTES.CONTENT.EDIT.QUESTION.replace(':id', String(item.value)) +
                      getDbTypeHashFragment?.('global-state', {}); // Fallback if getDbTypeHashFragment not in scope
                  }

                  return (
                    <div key={item.label} className='flex flex-col'>
                      <span className='text-xs font-medium uppercase tracking-wide text-default-500'>
                        {item.label}
                      </span>
                      {isQuestionId && item.value && questionLink ? (
                        <Link
                          target='_blank'
                          rel='noopener noreferrer'
                          to={questionLink}
                          className='flex items-center gap-2 text-default-800 hover:text-primary-600 hover:underline'
                        >
                          <span className='font-mono'>{item.value}</span>
                          <FiExternalLink className='size-3.5' />
                        </Link>
                      ) : (
                        <span className='text-default-800 break-all'>{item.value || '—'}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};
